// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
// URL des trois modules de la suite BIO24 (site · catalogue · Nafis),
// résolues À L'EXÉCUTION.
//
// ── Pourquoi ce fichier existe ─────────────────────────────────────────────
// Le 17/08 le laboratoire a renommé ses domaines (mdp.bio24.tn →
// catalog.bio24.tn, ged.bio24.tn → quality.bio24.tn, puis quality.bio24.tn →
// nafis.bio24.tn quand l'app est devenue la plateforme Nafis). Les liens entre modules
// sont restés cassés : `NEXT_PUBLIC_MANUEL_URL` & consorts sont INSCRITES DANS
// LE JAVASCRIPT AU MOMENT DE LA CONSTRUCTION. Changer la variable dans
// EasyPanel puis redémarrer ne produit rien — il faut reconstruire l'image.
// Une adresse qui change plus souvent que le code ne peut pas être une
// constante de compilation.
//
// Désormais : le serveur lit l'environnement à CHAQUE requête
// (`lireUrlsModules`) et pose le résultat dans un contexte React
// (`FournisseurUrlsModules`, posé par le layout racine de chaque app). Les
// composants clients lisent `useUrlsModules()`. Les bonnes URL sont donc
// présentes dès le premier rendu : pas de scintillement, pas d'appel réseau.
//
// ⚠️ `STRAPI_URL` / `STRAPI_GRAPHQL_URL` ne sont PAS concernées : elles sont
// déjà des variables d'exécution, lues côté serveur uniquement.
//
// ── Pourquoi pas de « use client » en tête ─────────────────────────────────
// Ce fichier est lu des DEUX côtés : `lireUrlsModules()` par des composants
// serveur (layouts, robots.ts, sitemap.ts), le contexte par des composants
// clients. Un module marqué « use client » ne peut pas être APPELÉ depuis le
// serveur (ses exports n'y sont que des références). La frontière client est
// donc portée par `urlsModulesClient.tsx`, un fichier de deux lignes qui ne
// fait que ré-exporter le fournisseur et le hook.
// Corollaire : le corps de ce fichier est évalué dans le graphe SERVEUR, où
// `React.createContext` n'existe pas — le contexte est donc créé à la demande
// (voir `contexteUrls`), c'est-à-dire uniquement côté client.
import React from 'react';

export type UrlsModules = { site: string; manuel: string; ged: string };

/** Valeurs de développement — les trois apps tournent côté à côté en local.
 *  Sert aussi de valeur par défaut du contexte : un composant client rendu
 *  hors du fournisseur retombe dessus au lieu de planter. */
const REPLI: UrlsModules = {
  site: 'http://localhost:3000',
  manuel: 'http://localhost:3001',
  ged: 'http://localhost:3002',
};

/** Lecture SERVEUR de l'environnement. À appeler au rendu de la requête, jamais
 *  au chargement du module : c'est la relecture À CHAQUE APPEL qui fait qu'un
 *  simple redémarrage du conteneur suffit à changer les adresses.
 *
 *  Ordre de priorité, par module :
 *    1. `SITE_URL_PUBLIC` / `MANUEL_URL_PUBLIC` / `GED_URL_PUBLIC` — variables
 *       d'EXÉCUTION, à poser dans « Environment » d'EasyPanel ;
 *    2. les `NEXT_PUBLIC_*_URL` historiques — repli qui garantit qu'aucun
 *       déploiement existant ne casse le jour de la bascule ;
 *    3. les valeurs de développement ci-dessus.
 *
 *  (Les `process.env.NEXT_PUBLIC_*` de cette fonction sont les SEULES du
 *  monorepo : elles ne sont plus lues qu'ici, en repli.)
 *
 *  ⚠️ Les `process.env.NEXT_PUBLIC_*` sont écrites EN TOUTES LETTRES, jamais
 *  via un alias (`const e = process.env; e.NEXT_PUBLIC_…`). Next remplace ces
 *  expressions par leur valeur à la construction, littéralement : passer par
 *  une variable intermédiaire empêche le remplacement, et le repli historique
 *  s'évanouirait en silence dans le conteneur — où les `NEXT_PUBLIC_*` ne sont
 *  posées qu'à l'étape de construction, pas à l'exécution. */
export function lireUrlsModules(): UrlsModules {
  return {
    site: process.env.SITE_URL_PUBLIC || process.env.NEXT_PUBLIC_SITE_URL || REPLI.site,
    manuel: process.env.MANUEL_URL_PUBLIC || process.env.NEXT_PUBLIC_MANUEL_URL || REPLI.manuel,
    ged: process.env.GED_URL_PUBLIC || process.env.NEXT_PUBLIC_GED_URL || REPLI.ged,
  };
}

// Contexte créé à la demande : `React.createContext` n'est pas disponible dans
// le graphe serveur, où ce module est pourtant importé pour `lireUrlsModules`.
// La création est mémorisée, l'identité du contexte reste donc unique dans le
// paquet client — condition pour que fournisseur et hook se parlent.
let contexte: React.Context<UrlsModules> | null = null;
function contexteUrls(): React.Context<UrlsModules> {
  contexte ??= React.createContext<UrlsModules>(REPLI);
  return contexte;
}

/** Posé par le layout racine de chaque app, avec `lireUrlsModules()` en valeur. */
export function FournisseurUrlsModules({ valeur, children }: {
  valeur: UrlsModules;
  children: React.ReactNode;
}) {
  const Ctx = contexteUrls();
  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

/** Les trois URL, côté client. Hors fournisseur, renvoie les valeurs de repli
 *  plutôt que de planter : un lien vers localhost se remarque et se corrige,
 *  un écran blanc coûte un appel. */
export function useUrlsModules(): UrlsModules {
  return React.useContext(contexteUrls());
}
